import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { 
    Box,
    IconButton,
    LinearProgress,
} from '@mui/material';
import {
    DataGridPro,
    LicenseInfo,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
    OpenInNew
} from '@mui/icons-material';
import { useAbortController } from '../../contexts/AbortControllerContext';
import { useFetch } from '../../components/auth/useFetch';
import ProjectContext from '../../contexts/ProjectContext';

LicenseInfo.setLicenseKey('17ca2e8476fa658618680f9f135dbc81T1JERVI6MzU2MTYsRVhQSVJZPTE4NzMzODQyMDIwMDAsS0VZVkVSU0lPTj0x',);

export default function IllusionOverviewDataGrid(props) {
    const { handleDetail, setLoading } = props
    const { isAuthenticated } = useAuth0();
    const projectContext = useContext(ProjectContext);
    const { registerController, unregisterController } = useAbortController()
    const fetchWithAuth = useFetch()
    const [loading, setIllusionLoading] = useState(false)
    const [rows, setRows] = React.useState([])

    const apiRef = useGridApiRef();
    const datagridStateRef = useRef(null)

    const columns = useMemo(() =>[
            {field: 'id',          headerName: 'EDL Name',       width: 250},
            {field: 'status',      headerName: 'Status',         width: 150, renderCell:(params) => 
                                                                                    <Box 
                                                                                        fontSize='8pt'
                                                                                        overflow="hidden"
                                                                                        border={1}
                                                                                        borderColor="rgba(255,255,255,0.8)"
                                                                                        borderRadius={1}
                                                                                        minWidth="100px"
                                                                                        maxWidth="100px"
                                                                                        minHeight="20px"
                                                                                        maxHeight="20px">
                                                                                            {params.row.status =='Complete' ?
                                                                                            <div style={{ width:"100px", height:"20px", background: "rgba(76, 175, 80, 1)", textAlign: "center", textJustify: "center"}}>
                                                                                                <Box 
                                                                                                    display="flex"
                                                                                                    alignItems="center"
                                                                                                    justifyContent="center"
                                                                                                    minWidth="100px"
                                                                                                    maxWidth="100px"
                                                                                                    minHeight="20px"
                                                                                                    maxHeight="20px">
                                                                                                    {params.row.status}
                                                                                                </Box>
                                                                                            </div>
                                                                                            :
                                                                                            (params.row.status =='Processing' || params.row.status == 'Ready') ?
                                                                                            <div style={{ width:"100px", height:"20px", background: "rgba(52, 155, 235, 1)", textAlign: "center", textJustify: "center"}}>
                                                                                                <Box 
                                                                                                    display="flex"
                                                                                                    alignItems="center"
                                                                                                    justifyContent="center"
                                                                                                    minWidth="100px"
                                                                                                    maxWidth="100px"
                                                                                                    minHeight="20px"
                                                                                                    maxHeight="20px">
                                                                                                    {params.row.status}
                                                                                                </Box>
                                                                                            </div>
                                                                                            :
                                                                                            <div style={{ width:"100px", height:"20px", background: "rgba(178, 235, 177, 1)", textAlign: "center", textJustify: "center"}}>
                                                                                                <Box 
                                                                                                    display="flex"
                                                                                                    alignItems="center"
                                                                                                    justifyContent="center"
                                                                                                    minWidth="100px"
                                                                                                    maxWidth="100px"
                                                                                                    minHeight="20px"
                                                                                                    maxHeight="20px">
                                                                                                    {params.row.status}
                                                                                                </Box>
                                                                                        </div>
                                                                                            }
                                                                                    </Box>}, 
            {field: 'submission',  headerName: 'Submission Date',   width: 200, valueFormatter: (params) => formatDateTime(params.value)},
            {field: 'submit_user', headerName: 'Submission User',   width: 200},
            {field: 'count',       headerName: 'Total Events',      width: 150},
            {field: 'total_amount',headerName: 'Total Frames',      width: 150},
            {field: 'pull_type',      headerName: 'Pull Type',            width: 150},
            {field: 'vendor',      headerName: 'Vendor',            width: 150},
            {field: 'details',     headerName: '', renderCell: (params) => 
                <IconButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        handleDetail(params.row.id);
                    }}>
                    <OpenInNew />
                </IconButton>
            , width: 10 },
        ], [])

    useEffect(() => {
        const intervalId = setInterval(retrieveAssetOverview, 15000);
        isAuthenticated && (() => {
            retrieveAssetOverview()
        })()
        return () => clearInterval(intervalId);  
    }, [isAuthenticated])

    useEffect(() => {
        if (datagridStateRef.current) {
            apiRef.current.restoreState(datagridStateRef.current);
        }
    }, [rows])


    const formatDateTime = (isoString) => {
        try {
            const date = new Date(isoString);
            if (isNaN(date)) return "-";
            const pad = n => n.toString().padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
                + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        } catch (e) {
            return "-";
        }
    };

    const handleRowDoubleClick = (event) => {
        handleDetail(event.id);
      };

    const retrieveAssetOverview = async () => {
        setLoading(true)
        setIllusionLoading(true)
        const edlSummaryController = registerController(``)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/edlsummary`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db
            },
            signal: edlSummaryController.signal
        })
            .then(response => response.json())
            .then(result => {
                setRows(result)
                // Handle loading anim time
                setIllusionLoading(false)
                setLoading(false)
            })
            .catch(e => {
                if(e.name !== 'AbortError'){
                    console.error(e);
                }
            })
            .finally(() => unregisterController(edlSummaryController))
    }

    return (
        <DataGridPro 
            sx={{
                backgroundColor: '#101010', // Change the background color of the data grid
                borderColor: '#122218',
                '& .MuiDataGrid-root': {
                    cursor: 'pointer', // Change the cursor to pointer when hovering over the data grid
                },
                '& .MuiDataGrid-cell:hover': {
                    color: 'rgb(76, 146, 107)',
                },
                '& .MuiDataGrid-columnSeparator': {
                    borderColor: 'green', // Make the separator green
                    borderWidth: '2px', // Set the thickness of the separator
                    visibility: 'visible', // Ensure the separator always visible
                    '&:hover': {
                        borderColor: 'rgb(76, 146, 107)', // Ensure the separator stays green on hover
                    },
                },
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#101010', // Change the column header background to black
                },
                '& .MuiDataGrid-columnHeader': {
                    fontSize: '14px', // Customize the font size of the column headers
                    color: 'white', // Change the column header color to white
                    fontWeight: 'bold', // Make the column header text bold
                    borderBottom: 'None', // Change the bottom line color of the column headers
                },
                '& .MuiDataGrid-sortIcon': {
                    color: 'rgba(255, 255, 255, 0.9)', // Change the color of the sort icon
                    fontSize: '12px'
                },
                '& .MuiDataGrid-row': {
                    border: 'none', // Remove the row separator lines
                },
                '& .MuiDataGrid-rowCount': {
                    color: 'white', // Change the background color of the selected row
                },
                '& .MuiDataGrid-selectedRowCount': {
                    color: 'white', // Change the font color of the selected row count
                },
                '& .MuiDataGrid-cell': {
                    border: 'none', // Remove the cell separator lines
                    color: 'white', // Change the font color of the cells
                    fontSize: '13px', // Make the cell text smaller
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                    backgroundColor: '#161618', // Change the background color of odd rows
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                    backgroundColor: '#202020', // Change the background color of even rows
                },
                '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(76, 146, 107, 0.1)', // Change the hover color of the row to light green with reduced opacity
                },
                '& .MuiDataGrid-cell--withRenderer': {
                    '& .MuiCheckbox-root': {
                        color: 'white', // Change the color of the checkbox
                        transform: 'scale(0.7)', // Make the checkbox smaller
                        '& .MuiSvgIcon-root': {
                            borderRadius: '50%', // Make the checkbox a circle
                        },
                        '&.Mui-checked': {
                            color: '#54a277', // Change the color of the selected checkbox to green
                        },
                    },
                },
                '& .MuiDataGrid-columnHeaderCheckbox': {
                    '& .MuiCheckbox-root': {
                        color: 'white', // Change the color of the checkbox in the column header
                        transform: 'scale(0.8)', // Make the checkbox smaller in the column header
                        '& .MuiSvgIcon-root': {
                            borderRadius: '50%', // Make the checkbox a circle in the column header
                        },
                        '&.Mui-checked': {
                            color: '#54a277', // Change the color of the selected checkbox to green in the column header
                        },
                    },
                },
            }}
            components={{ 
                LoadingOverlay: LinearProgress, 
            }}
            apiRef={apiRef}
            initialState={datagridStateRef.current}
            loading={loading}
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            onRowDoubleClick={handleRowDoubleClick}
            onSortModelChange={() => {
                datagridStateRef.current = apiRef.current.exportState();
            }}
            onColumnOrderChange={() => {
                datagridStateRef.current = apiRef.current.exportState();
            }}
            onColumnWidthChange={() => {
                datagridStateRef.current = apiRef.current.exportState();
            }}
            onFilterModelChange={() => {
                datagridStateRef.current = apiRef.current.exportState();
            }}
        />
    )
 }
